import { optional, withDependencies, named } from '@wix/thunderbolt-ioc'
import type { INavigation } from 'feature-navigation'
import { NavigationSymbol } from 'feature-navigation'
import { createLinkUtils } from '@wix/thunderbolt-commons'
import type { TpaHandlerProvider, IStructureAPI, Experiments } from '@wix/thunderbolt-symbols'
import {
	SiteFeatureConfigSymbol,
	StructureAPI,
	ExperimentsSymbol,
	SiteServicesManagerSymbol,
} from '@wix/thunderbolt-symbols'
import type { ServicesManager } from '@wix/services-manager/types'
import { LinkUtilsDefinition } from '@wix/viewer-service-link-utils/definition'
import type { IRoutingLinkUtilsAPI } from 'feature-router'
import { RoutingLinkUtilsAPISymbol } from 'feature-router'
import type { ILightboxUtils } from 'feature-lightbox'
import { LightboxUtilsSymbol } from 'feature-lightbox'
import type { LinkData, PageLinkData } from '@wix/thunderbolt-becky-types'
import type { IPageTransition } from 'feature-page-transitions'
import { PageTransitionsSymbol } from 'feature-page-transitions'
import type { TpaCommonsSiteConfig } from 'feature-tpa-commons'
import { name as tpaCommonsName } from 'feature-tpa-commons'
import { TPA_HANDLER_EMPTY_RESPONSE, LINK_TYPES } from '../utils/constants'
import type { IMultilingualLinkUtilsAPI } from 'feature-multilingual'
import { MultilingualLinkUtilsAPISymbol } from 'feature-multilingual'
import type { ICustomUrlMapper } from 'feature-custom-url-mapper'
import { CustomUrlMapperSymbol } from 'feature-custom-url-mapper'
import { isExperimentOpen } from '@wix/thunderbolt-experiments'

export type NavigateToMessageData = {
	link: LinkData
}

export type NavigateToAnchorMessageData = {
	anchorId: string
}

export type NavigateToPageMessageData = {
	pageId: string
	anchorId?: string
	noTransition?: boolean
}

export type NavigateToComponentMessageData = {
	compId: string
	pageId?: string
	noPageTransition?: boolean
}

export const NavigateToHandler = withDependencies(
	[
		named(SiteFeatureConfigSymbol, tpaCommonsName),
		NavigationSymbol,
		RoutingLinkUtilsAPISymbol,
		StructureAPI,
		ExperimentsSymbol,
		optional(LightboxUtilsSymbol),
		optional(MultilingualLinkUtilsAPISymbol),
		optional(PageTransitionsSymbol),
		optional(SiteServicesManagerSymbol),
		optional(CustomUrlMapperSymbol),
	],
	(
		tpaCommonsSiteConfig: TpaCommonsSiteConfig,
		navigation: INavigation,
		routingLinkUtilsAPI: IRoutingLinkUtilsAPI,
		structureApi: IStructureAPI,
		experiments: Experiments,
		lightboxUtils?: ILightboxUtils,
		multilingualLinkUtilsAPI?: IMultilingualLinkUtilsAPI,
		pageTransitions?: IPageTransition,
		servicesManager?: ServicesManager,
		customUrlMapper?: ICustomUrlMapper
	): TpaHandlerProvider => {
		const getLinkProps = (link: LinkData) => {
			const { metaSiteId, userFileDomainUrl, routersConfig, isMobileView, isPremiumDomain, isBuilderComponentModel } =
				tpaCommonsSiteConfig

			const isServicesInfra =
				isExperimentOpen(experiments, 'specs.thunderbolt.servicesInfra') || isBuilderComponentModel
			const useLegacyLinkUtilsInPlatform = isExperimentOpen(
				experiments,
				'specs.thunderbolt.useLegacyLinkUtilsInPlatform'
			)
			const shouldUseLinkUtilsService =
				servicesManager &&
				isServicesInfra &&
				!useLegacyLinkUtilsInPlatform &&
				servicesManager.hasService(LinkUtilsDefinition)

			const linkUtils = shouldUseLinkUtilsService
				? servicesManager.getService(LinkUtilsDefinition)
				: createLinkUtils({
						routingInfo: routingLinkUtilsAPI.getLinkUtilsRoutingInfo(),
						metaSiteId,
						userFileDomainUrl,
						routersConfig,
						popupPages: lightboxUtils?.getLightboxPages(),
						multilingualInfo: multilingualLinkUtilsAPI?.getMultilingualInfo(),
						isMobileView,
						isPremiumDomain,
						experiments,
						getIsUrlCustomized: customUrlMapper?.getIsUrlCustomized,
					})

			const linkUrl = linkUtils.getLinkUrlFromDataItem(link)
			return linkUtils.getLinkProps(linkUrl)
		}

		const getNavigateToPageLinkData = ({ pageId, anchorId }: { pageId: string; anchorId?: string }) => {
			const pageLinkData = { type: 'PageLink', pageId, target: '_self' } as PageLinkData
			const linkProps = getLinkProps(pageLinkData)

			if (anchorId) {
				if (routingLinkUtilsAPI.getLinkUtilsRoutingInfo().pageId === pageId) {
					linkProps.anchorCompId = anchorId
				} else {
					linkProps.anchorDataId = anchorId
				}
			}
			return linkProps
		}

		const navigateToPage = ({
			pageId,
			anchorId,
			noTransition,
		}: {
			pageId: string
			anchorId?: string
			noTransition?: boolean
		}) => {
			const linkProps = getNavigateToPageLinkData({ pageId, anchorId })

			if (noTransition && pageTransitions) {
				pageTransitions.disableNextTransition()
			}

			return navigation.navigateTo(linkProps)
		}

		return {
			getTpaHandlers() {
				return {
					navigateToPage(_compId: string, { pageId, anchorId, noTransition }: NavigateToPageMessageData) {
						navigateToPage({ pageId, anchorId, noTransition })
					},
					navigateTo(_compId: string, { link }: NavigateToMessageData) {
						if (link.type === LINK_TYPES.EXTERNAL_LINK) {
							// navigation is handled within the sdk due to popup blockers
							// https://github.com/wix-private/js-sdk/blob/8b2b20ba869214b82746f2b1a3eeb0216e67d70c/js/modules/Base.js#L241
							return
						}
						const linkProps = getLinkProps(link)
						navigation.navigateTo(linkProps)
					},
					async navigateToAnchor(_compId: string, { anchorId }: NavigateToAnchorMessageData) {
						const didNavigate = await navigateToPage({
							pageId: routingLinkUtilsAPI.getLinkUtilsRoutingInfo().pageId,
							anchorId,
						})
						if (!didNavigate) {
							throw new Error(`anchor with id "${anchorId}" was not found on the current page.`)
						}
						return TPA_HANDLER_EMPTY_RESPONSE
					},
					async navigateToComponent(
						_compId: string,
						{ compId, pageId: targetPageId, noPageTransition }: NavigateToComponentMessageData
					) {
						const page = targetPageId || routingLinkUtilsAPI.getLinkUtilsRoutingInfo().pageId
						const didNavigate = await navigateToPage({
							pageId: page,
							anchorId: compId,
							noTransition: noPageTransition,
						})
						if (!didNavigate) {
							throw new Error(`Page id "${page}" does not contain the component id "${compId}".`)
						}
						return TPA_HANDLER_EMPTY_RESPONSE
					},
				}
			},
		}
	}
)
